import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  Plugin,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { BehaviorType, GanttBarData, BEHAVIOR_LABELS, BEHAVIOR_COLORS, dogStateToBehaviorType, ThresholdConfig, DEFAULT_THRESHOLDS, BehaviorThreshold, fmtFreqLabel, frequencyToRate } from './behaviorTypes';
import { TimeRangePreset, getTimeRange } from '../../utils/timezone';

// Register Chart.js components
ChartJS.register(
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ─── Custom Plugin: Threshold Reference Lines ──────────────────────────────────
// Draws horizontal dashed lines at threshold values for each behavior.
// Threshold frequency (events per frequencyWindow) is converted to the chart's
// y-axis rate unit (events per Hour for <3D, events per Day for ≥3D) via
// frequencyToRate() so the line is directly comparable to the charted data.
// Only shows threshold line if: threshold.enabled && frequency is configured && behavior is visible

// Behavior label lookup for threshold line labels
const THRESHOLD_LABELS: Record<string, string> = {
  spin: 'Spin', toilet: 'Toilet', scratching: 'Scratch', walking: 'Walk',
  sitting: 'Sitting', standing: 'Standing', lying: 'Lying',
};

const thresholdLinePlugin: Plugin<'line'> = {
  id: 'thresholdLines',
  afterDraw(chart: any) {
    const thresholds = chart.options.plugins?.thresholdLines?.thresholds as ThresholdConfig | undefined;
    const visibleLines = chart.options.plugins?.thresholdLines?.visibleLines as Record<string, boolean> | undefined;
    const yAxisMinutes = chart.options.plugins?.thresholdLines?.yAxisMinutes as number | undefined;
    const shouldAggregate = chart.options.plugins?.thresholdLines?.shouldAggregate as boolean | undefined;
    if (!thresholds || !yAxisMinutes) return;

    const { ctx } = chart;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;

    // Map threshold config keys to (threshold, visibleKey) pairs.
    // visibleKey maps to the BehaviorType used in visibleLines state.
    const thresholdEntries: { key: string; threshold: BehaviorThreshold | undefined; visibleKey: string }[] = [
      { key: 'spin', threshold: thresholds.spin, visibleKey: 'spin' },
      { key: 'toilet', threshold: thresholds.toilet, visibleKey: 'toilet' },
      { key: 'scratching', threshold: thresholds.scratching, visibleKey: 'scratch' },
      { key: 'walking', threshold: thresholds.walking, visibleKey: 'walk' },
      { key: 'sitting', threshold: thresholds.sitting, visibleKey: 'sitting' },
      { key: 'standing', threshold: thresholds.standing, visibleKey: 'standing' },
      { key: 'lying', threshold: thresholds.lying, visibleKey: 'lying' },
    ];

    const rateUnit = shouldAggregate ? '/day' : '/hr';
    const xStart = xScale.left;
    const xEnd = xScale.right;

    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1.5;
    ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    for (const { key, threshold, visibleKey } of thresholdEntries) {
      const isEnabled = threshold?.enabled === true;
      const isVisible = visibleLines?.[visibleKey] !== false;
      if (!isEnabled || !isVisible) continue;

      const label = THRESHOLD_LABELS[key] ?? key;
      let lineIndex = 0;

      // Draw warning line (amber) if frequency threshold is configured
      if (threshold?.warning?.frequency != null) {
        const freq = threshold.warning.frequency;
        const window = threshold.warning.frequencyWindow;
        const thresholdY = frequencyToRate(freq, window, yAxisMinutes);
        if (thresholdY > 0) {
          const y = yScale.getPixelForValue(thresholdY);
          ctx.strokeStyle = 'rgba(245, 158, 11, 0.7)'; // amber-500
          ctx.fillStyle = 'rgba(245, 158, 11, 0.85)';
          ctx.beginPath();
          ctx.moveTo(xStart, y);
          ctx.lineTo(xEnd, y);
          ctx.stroke();
          ctx.textAlign = 'right';
          ctx.fillText(`${label} ⚠ ${thresholdY}${rateUnit}`, xEnd - 4, y - 4 - lineIndex * 12);
          lineIndex++;
        }
      }

      // Draw critical line (red) if frequency threshold is configured
      if (threshold?.critical?.frequency != null) {
        const freq = threshold.critical.frequency;
        const window = threshold.critical.frequencyWindow;
        const thresholdY = frequencyToRate(freq, window, yAxisMinutes);
        if (thresholdY > 0) {
          const y = yScale.getPixelForValue(thresholdY);
          ctx.strokeStyle = 'rgba(239, 68, 68, 0.7)'; // red-500
          ctx.fillStyle = 'rgba(239, 68, 68, 0.85)';
          ctx.beginPath();
          ctx.moveTo(xStart, y);
          ctx.lineTo(xEnd, y);
          ctx.stroke();
          ctx.textAlign = 'right';
          ctx.fillText(`${label} 🔴 ${thresholdY}${rateUnit}`, xEnd - 4, y - 4 - lineIndex * 12);
          lineIndex++;
        }
      }
    }

    ctx.restore();
  },
};

// ─── Time Ranges ───────────────────────────────────────────────────────────────

const TIME_RANGE_MINUTES: Record<TimeRangePreset, number> = {
  '1h': 60,
  '3h': 180,
  '6h': 360,
  '12h': 720,
  '24h': 1440,
  '3d': 4320,
  '7d': 10080,
  '30d': 43200,
};

// ─── Bin size for rate-based frequency chart ───────────────────────────────────
// For short ranges (<3D) the y-axis is "Events per Hour"; for long ranges (≥3D)
// it is "Events per Day". Each data point is a bin whose raw count is normalized
// to the y-axis rate unit.
const Y_AXIS_MINUTES_AGGREGATED = 1440; // per-day
const Y_AXIS_MINUTES_SHORT = 60;       // per-hour

/** Determine the bin size (in minutes) for a given time range. */
function getBinMinutes(timeRangeMinutes: number): number {
  if (timeRangeMinutes >= 4320) return 1440;  // 3D+ → daily bins
  if (timeRangeMinutes >= 720) return 120;    // 12h, 24h → 2-hour bins
  if (timeRangeMinutes >= 360) return 60;     // 6h → 1-hour bins
  if (timeRangeMinutes >= 180) return 30;     // 3h → 30-min bins
  return 10;                                  // 1h → 10-min bins
}

// ─── Tracked behaviors for frequency chart ─────────────────────────────────────
// Default behaviors shown when no DB data is available yet — matches common dog_state values
const DEFAULT_FREQUENCY_BEHAVIORS: BehaviorType[] = ['sitting', 'standing', 'lying', 'walk', 'scratch', 'unknown'];

// ─── Dog Pose Data Point (from Redux store) ───────────────────────────────────

interface DogPoseDataPoint {
  id?: number;
  sessionId?: string;
  deviceName?: string;
  timestamp: number;
  startedAt?: number;
  dogState?: string;
  isValid?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface BehaviorFrequencyChartProps {
  events?: GanttBarData[];
  thresholds?: ThresholdConfig;
  /** Time range preset from parent (shared with BehaviorSummary) */
  timeRange?: TimeRangePreset;
  /** Timezone from parent */
  timezone?: string;
  /** Custom time range (from/to epoch ms). When provided, overrides the preset. */
  customTimeRange?: { from: number; to: number } | null;
  /** Dog pose data from Redux store — used when available */
  dogPoses?: DogPoseDataPoint[];
  /** Distinct dog_state values from DB — used to build dynamic filter buttons */
  distinctDogStates?: string[];
  /** Visible lines filter state (persisted from profile) */
  visibleLinesOverride?: Record<string, boolean>;
  /** Callback when visible lines change (for persistence) */
  onVisibleLinesChange?: (visibleLines: Record<string, boolean>) => void;
}

const BehaviorFrequencyChart: React.FC<BehaviorFrequencyChartProps> = ({
  events,
  thresholds = DEFAULT_THRESHOLDS,
  timeRange: timeRangePreset = '24h',
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  customTimeRange = null,
  dogPoses,
  distinctDogStates,
  visibleLinesOverride,
  onVisibleLinesChange,
}) => {
  // Convert preset to minutes for internal calculations, or use custom range duration
  const timeRange = customTimeRange
    ? Math.max(1, Math.round((customTimeRange.to - customTimeRange.from) / 60000))
    : TIME_RANGE_MINUTES[timeRangePreset] ?? 1440;

  // Determine which behavior types to show as filter buttons
  // If distinctDogStates from DB is available, map them to BehaviorType; otherwise use defaults
  const filterBehaviors = useMemo(() => {
    if (distinctDogStates && distinctDogStates.length > 0) {
      // Map dog_state values to BehaviorType, deduplicate, and keep order
      const mapped = distinctDogStates
        .map(ds => dogStateToBehaviorType(ds))
        .filter((bt, idx, arr) => arr.indexOf(bt) === idx); // deduplicate
      return mapped;
    }
    return DEFAULT_FREQUENCY_BEHAVIORS;
  }, [distinctDogStates]);

  const [visibleLines, setVisibleLinesLocal] = useState<Record<string, boolean>>({});

  // Initialize visibleLines when filterBehaviors changes or override is provided
  React.useEffect(() => {
    setVisibleLinesLocal(prev => {
      const next: Record<string, boolean> = {};
      filterBehaviors.forEach(bt => {
        // Use override if provided, otherwise preserve existing or default to true
        if (visibleLinesOverride && visibleLinesOverride[bt] !== undefined) {
          next[bt] = visibleLinesOverride[bt];
        } else if (prev[bt] !== undefined) {
          next[bt] = prev[bt];
        } else {
          next[bt] = true;
        }
      });
      return next;
    });
  }, [filterBehaviors, visibleLinesOverride]);

  // Wrapper that also calls the callback
  const setVisibleLines = useCallback((update: React.SetStateAction<Record<string, boolean>>) => {
    setVisibleLinesLocal(prev => {
      const next = typeof update === 'function' ? update(prev) : update;
      onVisibleLinesChange?.(next);
      return next;
    });
  }, [onVisibleLinesChange]);

  const chartRef = useRef<any>(null);

  // Determine if we should aggregate by day
  const shouldAggregate = timeRange >= 4320; // 3D+

  // Y-axis rate unit: events per hour (<3D) or events per day (≥3D)
  const yAxisMinutes = shouldAggregate ? Y_AXIS_MINUTES_AGGREGATED : Y_AXIS_MINUTES_SHORT;
  const binMinutes = getBinMinutes(timeRange);
  const binMs = binMinutes * 60 * 1000;

  // Calculate time window — use custom range if provided, otherwise derive from preset
  const timeWindow = useMemo(() => {
    if (customTimeRange) {
      return {
        timeStart: customTimeRange.from,
        timeEnd: customTimeRange.to,
      };
    }
    const range = getTimeRange(timeRangePreset, timezone);
    return {
      timeStart: range.from,
      timeEnd: range.to,
    };
  }, [customTimeRange, timeRangePreset, timezone]);

  // Build pose data from real DB data or fall back to events prop
  const sourcePoses = useMemo(() => {
    if (dogPoses && dogPoses.length > 0) {
      // Filter poses within the time window
      return dogPoses.filter(p => {
        const ts = p.startedAt ?? p.timestamp;
        return ts >= timeWindow.timeStart && ts <= timeWindow.timeEnd;
      });
    }
    return null; // No DB data available
  }, [dogPoses, timeWindow]);

  // Toggle line visibility
  const toggleLine = useCallback((type: string) => {
    setVisibleLines(prev => ({ ...prev, [type]: !prev[type] }));
  }, []);

  // Build chart data from real DB poses or from events prop
  const chartData = useMemo(() => {
    // Collect timestamps per behavior from either DB poses or gantt-bar events.
    // Each entry is { ts: epochMs } so we can bucket uniformly.
    const timestampsByBehavior = new Map<BehaviorType, number[]>();
    let hasSource = false;

    if (sourcePoses && sourcePoses.length > 0) {
      hasSource = true;
      for (const pose of sourcePoses) {
        const bt = dogStateToBehaviorType(pose.dogState ?? 'unsure');
        const ts = pose.startedAt ?? pose.timestamp;
        if (!timestampsByBehavior.has(bt)) timestampsByBehavior.set(bt, []);
        timestampsByBehavior.get(bt)!.push(ts);
      }
    } else if (events && events.length > 0) {
      hasSource = true;
      for (const ev of events) {
        if (!timestampsByBehavior.has(ev.type)) timestampsByBehavior.set(ev.type, []);
        timestampsByBehavior.get(ev.type)!.push(ev.startTime);
      }
    }

    if (!hasSource) {
      // No data at all — return empty datasets
      return { datasets: filterBehaviors.map((type) => ({
        label: BEHAVIOR_LABELS[type],
        data: [],
        borderColor: BEHAVIOR_COLORS[type].normal,
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        pointRadius: 3,
        hidden: !visibleLines[type],
      })) };
    }

    const numBins = Math.max(1, Math.ceil(timeRange / binMinutes));
    const normalizationFactor = yAxisMinutes / binMinutes;

    const datasets = filterBehaviors.map((type) => {
      const typeTimestamps = timestampsByBehavior.get(type) ?? [];
      const binned: { x: number; y: number }[] = [];

      for (let b = 0; b < numBins; b++) {
        const binStart = timeWindow.timeStart + b * binMs;
        const binEnd = binStart + binMs;
        const count = typeTimestamps.filter(ts => ts >= binStart && ts < binEnd).length;
        // Normalize raw bin count to the y-axis rate unit
        const rate = Math.round(count * normalizationFactor * 100) / 100;
        binned.push({ x: binStart + binMs / 2, y: rate });
      }

      return {
        label: BEHAVIOR_LABELS[type],
        data: binned,
        borderColor: BEHAVIOR_COLORS[type].normal,
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        pointRadius: shouldAggregate ? 4 : 3,
        pointHoverRadius: shouldAggregate ? 7 : 6,
        pointBackgroundColor: BEHAVIOR_COLORS[type].normal,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        fill: false,
        tension: 0.3,
        spanGaps: true,
        hidden: !visibleLines[type],
      };
    });

    return { datasets };
  }, [sourcePoses, events, filterBehaviors, timeWindow, shouldAggregate, visibleLines, binMinutes, binMs, yAxisMinutes, timeRange, customTimeRange]);

  // Chart options
  const options = useMemo((): ChartOptions<'line'> => {
    const timeUnit = timeRange <= 60 ? 'minute' : timeRange <= 720 ? 'hour' : timeRange <= 1440 ? 'hour' : 'day';
    const displayFormats = {
      minute: 'HH:mm',
      hour: 'HH:mm',
      day: 'MMM d',
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      scales: {
        x: {
          type: 'time' as const,
          time: {
            unit: timeUnit,
            displayFormats,
          },
          min: timeWindow.timeStart,
          max: timeWindow.timeEnd,
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
          },
          ticks: {
            color: 'rgba(156, 163, 175, 0.8)',
            font: { size: 10 },
            maxRotation: 45,
            autoSkip: true,
            maxTicksLimit: timeRange >= 43200 ? 15 : timeRange >= 10080 ? 10 : undefined,
          },
          title: {
            display: false,
          },
        },
        y: {
          type: 'linear' as const,
          beginAtZero: true,
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
          },
          ticks: {
            color: 'rgba(156, 163, 175, 0.8)',
            font: { size: 11 },
            callback: (value: number | string) => {
              const numVal = typeof value === 'string' ? parseFloat(value) : value;
              // Show integers and half-integers (e.g. 0.5) for rate axis
              if (numVal >= 0 && (Number.isInteger(numVal) || numVal * 2 === Math.floor(numVal * 2))) {
                return numVal;
              }
              return '';
            },
          },
          title: {
            display: true,
            text: shouldAggregate ? 'Events per Day' : 'Events per Hour',
            color: 'rgba(156, 163, 175, 0.8)',
            font: { size: 11 },
          },
        },
      },
      plugins: {
        legend: {
          display: false, // We render our own toggle UI
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(15, 23, 42, 0.92)',
          borderColor: 'rgba(148, 163, 184, 0.3)',
          borderWidth: 1,
          titleColor: '#f1f5f9',
          bodyColor: '#cbd5e1',
          titleFont: { size: 13, weight: '600' as const },
          bodyFont: { size: 12 },
          padding: 10,
          cornerRadius: 8,
          displayColors: true,
          filter: (tooltipItem: any) => {
            // Only show tooltip for visible datasets
            return !tooltipItem.dataset.hidden;
          },
          callbacks: {
            title: (items: any) => {
              if (items.length > 0) {
                const date = new Date(items[0].parsed.x);
                if (shouldAggregate) {
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }
                return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
              }
              return '';
            },
            label: (context: any) => {
              if (shouldAggregate) {
                return ` ${context.dataset.label}: ${context.parsed.y} events/day`;
              }
              return ` ${context.dataset.label}: ${context.parsed.y} events/hour`;
            },
          },
        },
        thresholdLines: {
          thresholds,
          visibleLines,
          yAxisMinutes,
          shouldAggregate,
        },
      } as any,
      animation: {
        duration: 300,
      },
    } as ChartOptions<'line'>;
  }, [timeWindow, timeRange, thresholds, visibleLines, shouldAggregate, yAxisMinutes]);

  const hasData = chartData.datasets.some((ds) => ds.data.length > 0);

  // Format the current range label for the subtitle
  const rangeLabel = useMemo(() => {
    if (customTimeRange) {
      const fmt = (ms: number) => new Date(ms).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
      });
      return `${fmt(customTimeRange.from)} → ${fmt(customTimeRange.to)}`;
    }
    const tr = getTimeRange(timeRangePreset, timezone);
    return tr.label;
  }, [customTimeRange, timeRangePreset, timezone]);

  return (
    <div className="rounded-xl dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted p-6 border border-claude-border dark:border-claude-darkBorder">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">
            Behavior Frequency
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {shouldAggregate ? 'Daily event rate' : 'Hourly event rate'} — {rangeLabel}
          </p>
        </div>
      </div>

      {/* Line visibility toggles */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {filterBehaviors.map((type) => (
          <button
            key={type}
            onClick={() => toggleLine(type)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
              visibleLines[type]
                ? 'border-transparent shadow-sm'
                : 'border-gray-200 dark:border-gray-600 opacity-50'
            }`}
            style={{
              backgroundColor: visibleLines[type] ? BEHAVIOR_COLORS[type].normal + '20' : 'transparent',
              color: visibleLines[type] ? BEHAVIOR_COLORS[type].normal : undefined,
            }}
          >
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{
                backgroundColor: visibleLines[type] ? BEHAVIOR_COLORS[type].normal : '#9ca3af',
              }}
            />
            {BEHAVIOR_LABELS[type]}
            {visibleLines[type] ? (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        ))}
      </div>

      {/* Chart */}
      {hasData ? (
        <div style={{ height: '280px', position: 'relative' }}>
          <Line
            ref={chartRef}
            data={chartData as any}
            options={options as any}
            plugins={[thresholdLinePlugin]}
          />
        </div>
      ) : (
        <div className="h-48 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
          <svg
            className="w-12 h-12 mb-3 opacity-40"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className="text-sm">No frequency data available</p>
          <p className="text-xs mt-1">Start a session to track behavior frequency</p>
        </div>
      )}

      {/* Threshold Legend */}
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0 border-t-2 border-dashed border-amber-400 opacity-70" />
          <span className="text-gray-500 dark:text-gray-400">Warning</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0 border-t-2 border-dashed border-red-400 opacity-70" />
          <span className="text-gray-500 dark:text-gray-400">Critical</span>
        </div>
        <div className="text-gray-400 dark:text-gray-500">
          Lying: {thresholds.lying?.warning?.duration ?? thresholds.lying?.critical?.duration ?? '—'}min • Sitting: {thresholds.sitting?.warning?.duration ?? thresholds.sitting?.critical?.duration ?? '—'}min • Standing: {thresholds.standing?.warning?.duration ?? thresholds.standing?.critical?.duration ?? '—'}min • Walking: {fmtFreqLabel(thresholds.walking?.warning?.frequency ?? thresholds.walking?.critical?.frequency, thresholds.walking?.warning?.frequencyWindow ?? thresholds.walking?.critical?.frequencyWindow)} • Scratching: {fmtFreqLabel(thresholds.scratching?.warning?.frequency ?? thresholds.scratching?.critical?.frequency, thresholds.scratching?.warning?.frequencyWindow ?? thresholds.scratching?.critical?.frequencyWindow)}
        </div>
      </div>
    </div>
  );
};

export default BehaviorFrequencyChart;